import { invertObj, map, prop } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getSearchIndexConfigFactory, getSearchIndexName } from '../../config/searchIndex/searchIndex.ts';
import { useSearchDomains } from '../../hooks/useSearchDomains/useSearchDomains.ts';
import { SearchConfigGetter } from '../../types/SearchConfigGetter.ts';
import { SearchConfigItem } from '../../types/SearchConfigItem.ts';
import { SearchDomain } from '../../types/SearchDomain.ts';

import { getSortByMaps, SearchSortByMaps } from './useSearchConfigMapper.util.ts';

interface SearchSlugMapper {
  sortBy: SearchSortByMaps;
}

export interface UseSearchConfigMapperResult {
  searchConfigByDomain: SearchConfigGetter;
  domainByIndexName: Record<string, SearchDomain>;
  sortByValueToSlug: (searchDomain: SearchDomain, orderByValue: string | undefined) => string | undefined;
  sortBySlugToValue: (searchDomain: SearchDomain, slug: string | undefined) => string | undefined;
}

export const useSearchConfigMapper = (): UseSearchConfigMapperResult => {
  const { t } = useTranslation(undefined, { keyPrefix: 'SearchDialog' });
  const { searchDomains } = useSearchDomains();

  const searchConfigMap = useMemo<Record<SearchDomain, SearchConfigItem>>(() => {
    return searchDomains.reduce(
      (acc, searchDomain) => ({ ...acc, [searchDomain]: getSearchIndexConfigFactory(searchDomain)({ t }) }),
      {} as Record<SearchDomain, SearchConfigItem>,
    );
  }, [t, searchDomains]);

  return useMemo(() => {
    const slugMapsBySearchDomain = searchDomains.reduce(
      (acc, searchDomain) => {
        const config = searchConfigMap[searchDomain];

        const mapper: SearchSlugMapper = {
          sortBy: getSortByMaps(config.sortByItems),
        };

        return { ...acc, [searchDomain]: mapper };
      },
      {} as Record<SearchDomain, SearchSlugMapper>,
    );
    return {
      searchConfigByDomain: (searchDomain) => {
        return searchConfigMap[searchDomain];
      },
      // Using fallback on the indexName as this represents an index default sorting in the #valueToSlug mapping
      sortByValueToSlug: (searchDomain, orderByValue = getSearchIndexName(searchDomain)) => {
        const { sortBy } = slugMapsBySearchDomain[searchDomain];

        if (orderByValue === sortBy.defaultValue) {
          return undefined;
        }

        return orderByValue && sortBy.valueToSlug[orderByValue];
      },
      sortBySlugToValue: (searchDomain, slug) => {
        const { sortBy } = slugMapsBySearchDomain[searchDomain];
        if (!slug) {
          return sortBy.defaultValue;
        }
        return sortBy.slugToValue[slug];
      },

      domainByIndexName: invertObj(map(prop('indexName'), searchConfigMap)) as Record<string, SearchDomain>,
    } satisfies UseSearchConfigMapperResult;
  }, [searchConfigMap, searchDomains]);
};
